/**
 * Shared URL-state layer for intra-tab navigation.
 *
 * Canonical model (docs/specs/architecture/navigation-deeplink-spec.md):
 *  - tab        = path segment 0          (useTabRouter)
 *  - selection  = a query param, `push`   (useSelectionParam — the thing you click into)
 *  - view state = a query param           (useViewParam — push for discrete views,
 *                                           replace for incremental text input)
 *  - nav-context = `intent` / `return_to` (§3.6 — useReturnTo + per-page parsers)
 *
 * Every programmatic param write dispatches an `ember:nav` CustomEvent so sibling
 * hooks on the same page resync without prop-drilling. `useTabRouter` intentionally
 * does NOT listen to `ember:nav` (these hooks only ever rewrite query params, never
 * the path — spec §12 finding 1).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_TAB, isTabKey, navigateTab, type TabKey } from '../useTabRouter';

export type HistoryMode = 'push' | 'replace';

/** Dispatched after any programmatic query-param write so sibling hooks resync. */
export const EMBER_NAV_EVENT = 'ember:nav';

function readParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
}

function emitNav(): void {
  window.dispatchEvent(new CustomEvent(EMBER_NAV_EVENT));
}

/**
 * Write a single query param, preserving siblings + hash. Reads LIVE
 * window.location at call time (so two same-tick writes compose — the second
 * sees the first's synchronous pushState/replaceState). Skips the write when the
 * value is unchanged (no spurious history entry). `serialized === null` deletes
 * the param. Returns whether a write happened.
 */
function writeParam(key: string, serialized: string | null, mode: HistoryMode): boolean {
  const url = new URL(window.location.href);
  const current = url.searchParams.get(key);
  if (serialized === current) return false; // unchanged (covers both null===null and equal strings)
  if (serialized === null) url.searchParams.delete(key);
  else url.searchParams.set(key, serialized);
  const next = url.pathname + url.search + url.hash;
  // Preserve history.state so we never clobber a selection marker on a replace.
  // A `push` view-write starts a fresh entry with null state (markers belong to
  // selections only).
  if (mode === 'push') window.history.pushState(null, '', next);
  else window.history.replaceState(window.history.state, '', next);
  emitNav();
  return true;
}

interface UrlStateOptions<T> {
  parse: (raw: string | null) => T;
  /** value → URL string, or null to remove the param. */
  serialize: (value: T) => string | null;
  /** Default write mode. Per-call override available on the setter. */
  history?: HistoryMode;
}

/**
 * Bind a single typed value to one query param. The workhorse behind
 * useViewParam / useSelectionParam.
 */
export function useUrlState<T>(
  key: string,
  opts: UrlStateOptions<T>,
): [T, (next: T, override?: { history?: HistoryMode }) => void] {
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });

  const [value, setLocal] = useState<T>(() => opts.parse(readParam(key)));

  useEffect(() => {
    const resync = () => setLocal(optsRef.current.parse(readParam(key)));
    window.addEventListener('popstate', resync);
    window.addEventListener(EMBER_NAV_EVENT, resync);
    return () => {
      window.removeEventListener('popstate', resync);
      window.removeEventListener(EMBER_NAV_EVENT, resync);
    };
  }, [key]);

  const set = useCallback(
    (next: T, override?: { history?: HistoryMode }) => {
      const mode = override?.history ?? optsRef.current.history ?? 'replace';
      writeParam(key, optsRef.current.serialize(next), mode);
      setLocal(next); // immediate; the dispatched ember:nav also resyncs siblings
    },
    [key],
  );

  return [value, set];
}

/**
 * View state (sub-tab, mode, filter chip, sort, group, search). Default `push`
 * so discrete view switches are Back-able; pass `{history:'replace'}` for
 * incremental text input (the search box) so typing doesn't spam history.
 * Absent/`fallback` value ⇒ param removed (clean URLs).
 */
export function useViewParam(
  key: string,
  fallback: string,
  opts?: { history?: HistoryMode },
): [string, (v: string) => void] {
  return useUrlState<string>(key, {
    parse: (raw) => raw ?? fallback,
    serialize: (v) => (v === fallback ? null : v),
    history: opts?.history ?? 'push',
  });
}

/**
 * Selection — the entity you click into (deal_key, review, campaign, …).
 * `open()` pushes a new history entry with a `{ emberSel: key }` marker so
 * `close()` knows it owns the top entry and can `history.back()` (returning to
 * the originating list). When there's no marker — an externally-pasted deep link
 * or a post-MSAL restore (state is null) — `close()` instead drops the param via
 * replaceState, so it never `back()`s the user out of the app (spec §4.2).
 */
export function useSelectionParam(key: string): {
  value: string | null;
  open: (id: string, opts?: { replace?: boolean }) => void;
  close: () => void;
} {
  const [value, setValue] = useState<string | null>(() => readParam(key));

  useEffect(() => {
    const resync = () => setValue(readParam(key));
    window.addEventListener('popstate', resync);
    window.addEventListener(EMBER_NAV_EVENT, resync);
    return () => {
      window.removeEventListener('popstate', resync);
      window.removeEventListener(EMBER_NAV_EVENT, resync);
    };
  }, [key]);

  const open = useCallback(
    (id: string, opts?: { replace?: boolean }) => {
      const url = new URL(window.location.href);
      if (url.searchParams.get(key) === id) return; // already open on this entry
      url.searchParams.set(key, id);
      const next = url.pathname + url.search + url.hash;
      // NOTE: when the debounced search-in-URL lands (fast-follow), open() must
      // first flush any pending debounced view-writes so they land on THIS entry
      // (spec §12 finding 2). No debounced view params exist in the current scope.
      if (opts?.replace) {
        // Conveyor advance (e.g. "Next pending update") — swap the selection on
        // the current entry instead of pushing, so Back doesn't walk back through
        // already-cleared cards. Preserve the existing marker.
        window.history.replaceState(window.history.state, '', next);
      } else {
        window.history.pushState({ emberSel: key }, '', next);
      }
      emitNav();
      setValue(id);
    },
    [key],
  );

  const close = useCallback(() => {
    const st = window.history.state as { emberSel?: string } | null;
    if (st && st.emberSel === key) {
      window.history.back(); // pop our pushed entry → popstate restores the list
      return;
    }
    const url = new URL(window.location.href);
    if (!url.searchParams.has(key)) return;
    url.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    emitNav();
    setValue(null);
  }, [key]);

  return { value, open, close };
}

/**
 * Navigation-context: reads `?return_to=<tab>` and powers in-app "Back to X"
 * affordances (NOT the browser Back button). `goBack()` navigates to the
 * return target only when it's a valid TabKey; otherwise it falls back to the
 * default tab (Pipeline). Never feeds an unvalidated string into navigation
 * (spec §3.6 / §13, Eng F3.1).
 */
export function useReturnTo(): { target: TabKey | null; goBack: () => void } {
  const raw = readParam('return_to');
  const target = isTabKey(raw) ? raw : null;

  const goBack = useCallback(() => {
    const rt = readParam('return_to');
    navigateTab(isTabKey(rt) ? rt : DEFAULT_TAB);
  }, []);

  return { target, goBack };
}
